import React from 'react';
import { motion } from 'framer-motion';
import { Compass, CheckCircle2, AlertTriangle, ShieldCheck, Leaf, FlaskConical, MessageCircle } from 'lucide-react';
const discoveries = [{
  icon: FlaskConical,
  text: 'Mineral forms with better absorption'
}, {
  icon: AlertTriangle,
  text: 'Filler ingredients you should avoid'
}, {
  icon: ShieldCheck,
  text: 'Misleading doses and proprietary blends'
}, {
  icon: CheckCircle2,
  text: 'Certifications that actually matter'
}, {
  icon: Leaf,
  text: 'Supplements made from organic fruits and vegetables that often offer a cleaner approach more aligned with real food'
}];
export function BuyersCompass() {
  return <section className="py-24 bg-forest text-offwhite px-6 lg:px-8" data-id="element-16">
      <div className="max-w-4xl mx-auto" data-id="element-17">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-12" data-id="element-18">
          <div className="inline-flex items-center gap-2 bg-amber/20 text-amber px-4 py-2 rounded-full text-sm font-bold tracking-wider uppercase mb-6" data-id="element-19">
            <Compass size={18} data-id="element-20" />
            Bonus Guide
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-offwhite mb-4" data-id="element-21">
            The Buyer's Compass
          </h2>
          <p className="text-xl md:text-2xl font-serif italic text-amber" data-id="element-22">
            How to Read Supplement Labels Without Being an Expert
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.1
      }} className="bg-white/5 border border-white/10 rounded-3xl p-8 md:p-12 mb-12" data-id="element-23">
          <p className="text-lg text-amber font-bold mb-6" data-id="element-24">
            The 60-second filter to stop wasting your money on "cheap energy"
          </p>
          <p className="text-offwhite/80 text-lg leading-relaxed mb-6" data-id="element-25">
            Your body deserves micronutrients, not chemical fillers.
          </p>
          <p className="text-offwhite/80 leading-relaxed mb-6" data-id="element-26">
            Have you ever bought supplements and felt no difference? It's not
            always because they "don't work." Many times, the real problem is
            that you bought a formula that looks good on the outside… but is
            weak on the inside.
          </p>
          <p className="text-offwhite/80 leading-relaxed mb-6" data-id="element-27">
            The truth is simple:{' '}
            <strong className="text-offwhite" data-id="element-28">
              Most labels are designed to sell, not to educate you.
            </strong>
          </p>
          <p className="text-offwhite/80 leading-relaxed" data-id="element-29">
            They promise energy, focus, and vitality, but hide cheap
            ingredients, low-absorption forms, and non-transparent blends. And
            if you don't know how to read a label, it's very easy to spend money
            on supplements that simply aren't worth it.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.2
      }} className="mb-12" data-id="element-30">
          <p className="text-xl font-serif text-amber mb-8" data-id="element-31">
            That's why I created The Buyer's Compass — a practical guide to help
            you evaluate supplements in less than 60 seconds, without being an
            expert.
          </p>

          <h3 className="text-2xl font-serif font-bold text-offwhite mb-6" data-id="element-32">
            Inside, you'll discover how to identify:
          </h3>
          <div className="space-y-4" data-id="element-33">
            {discoveries.map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            x: -10
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1 * index
          }} className="flex items-start gap-4 bg-white/5 border border-white/10 p-5 rounded-xl" data-id="element-34">
                <item.icon className="text-amber shrink-0 mt-0.5" size={22} data-id="element-35" />
                <span className="text-offwhite/90 leading-relaxed" data-id="element-36">
                  {item.text}
                </span>
              </motion.div>)}
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.3
      }} className="space-y-8" data-id="element-37">
          <div className="bg-white/5 border border-white/10 rounded-3xl p-8 md:p-10" data-id="element-38">
            <h3 className="text-2xl font-serif font-bold text-offwhite mb-4" data-id="element-39">
              This guide helps you buy with better judgment
            </h3>
            <p className="text-offwhite/80 leading-relaxed mb-4" data-id="element-40">
              No more being influenced by a pretty bottle. No more assuming that
              "more expensive" means "better." No more putting something into
              your body that you don't understand.
            </p>
            <p className="text-offwhite/80 leading-relaxed" data-id="element-41">
              With this guide, you'll learn to look past the marketing and
              choose formulas with more clarity and confidence.
            </p>
          </div>

          <div className="bg-amber/10 border border-amber/20 rounded-3xl p-8 md:p-10" data-id="element-42">
            <div className="flex items-center gap-3 mb-4" data-id="element-43">
              <MessageCircle className="text-amber" size={24} data-id="element-44" />
              <h3 className="text-2xl font-serif font-bold text-offwhite" data-id="element-45">
                And if you want personalized help…
              </h3>
            </div>
            <p className="text-offwhite/80 leading-relaxed mb-4" data-id="element-46">
              Contact me, and I'll be happy to guide you with multivitamin and
              mineral recommendations based on this guide and adapted to your
              personal needs.
            </p>
            <p className="text-offwhite/80 leading-relaxed" data-id="element-47">
              Just message me and tell me a little about your routine, your
              energy, and what you're looking for.
            </p>
          </div>

          <div className="text-center pt-4" data-id="element-48">
            <p className="text-2xl font-serif font-bold text-amber mb-2" data-id="element-49">
              Stop buying promises. Start buying formulas.
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}